import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  History as HistoryIcon,
  Person as PersonIcon,
  Computer as ComputerIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';

const UserActivityTracking = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Filter states
  const [filterUserId, setFilterUserId] = useState('');
  const [filterUsername, setFilterUsername] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  
  // User list for filter
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchActivities();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (filterUserId) params.userId = filterUserId;
      if (filterUsername) params.username = filterUsername;
      if (filterRole) params.role = filterRole;
      // Set start date to beginning of day and end date to end of day
      if (filterStartDate) {
        const start = new Date(filterStartDate);
        start.setHours(0, 0, 0, 0);
        params.startDate = start.toISOString();
      }
      if (filterEndDate) {
        const end = new Date(filterEndDate);
        end.setHours(23, 59, 59, 999);
        params.endDate = end.toISOString();
      }
      
      let response;
      if (showActiveOnly) {
        response = await api.get('/admin/user-activities/active');
      } else {
        response = await api.get('/admin/user-activities', { params });
      }
      
      setActivities(response.data || []);
    } catch (error) {
      console.error('Error fetching user activities:', error);
      showToast('Failed to load user activities', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (activity) => {
    setSelectedActivity(activity);
    setDetailDialogOpen(true);
  };

  const handleClearFilters = () => {
    setFilterUserId('');
    setFilterUsername('');
    setFilterRole('');
    setFilterStartDate('');
    setFilterEndDate('');
    setShowActiveOnly(false);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    // Convert to Sri Lankan timezone (Asia/Colombo)
    const date = new Date(dateString);
    // Format in Sri Lankan timezone
    return date.toLocaleString('en-LK', {
      timeZone: 'Asia/Colombo',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}min`;
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'error';
      case 'LOAN_OFFICER':
        return 'primary';
      case 'FIELD_OFFICER':
        return 'success';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'Admin';
      case 'LOAN_OFFICER':
        return 'Loan Officer';
      case 'FIELD_OFFICER':
        return 'Field Officer';
      default:
        return role;
    }
  };

  // Calculate statistics
  const totalSessions = activities.length;
  const activeSessions = activities.filter(a => a.isActive).length;
  const totalUsers = new Set(activities.map(a => a.userId)).size;
  
  // Calculate total active hours accurately
  const calculateTotalActiveHours = () => {
    let totalMinutes = 0;
    
    activities.forEach(activity => {
      if (!activity.loginTime) return; // Skip if no login time
      
      const loginTime = new Date(activity.loginTime);
      
      if (activity.isActive) {
        // For active sessions, calculate from login time to lastActiveTime (or current time if lastActiveTime is not available)
        let endTime;
        if (activity.lastActiveTime) {
          endTime = new Date(activity.lastActiveTime);
        } else {
          // If lastActiveTime is not available, use current time
          endTime = new Date();
        }
        
        const durationMs = endTime - loginTime;
        const durationMinutes = Math.max(0, Math.floor(durationMs / (1000 * 60))); // Ensure non-negative
        totalMinutes += durationMinutes;
      } else {
        // For closed sessions, use the stored sessionDurationMinutes if available
        // Otherwise, calculate from loginTime to logoutTime
        if (activity.sessionDurationMinutes != null && activity.sessionDurationMinutes > 0) {
          // Use the stored session duration (already calculated accurately on backend)
          totalMinutes += activity.sessionDurationMinutes;
        } else if (activity.logoutTime) {
          // Fallback: calculate from loginTime to logoutTime if sessionDurationMinutes is not available
          const logoutTime = new Date(activity.logoutTime);
          const durationMs = logoutTime - loginTime;
          const durationMinutes = Math.max(0, Math.floor(durationMs / (1000 * 60)));
          totalMinutes += durationMinutes;
        }
      }
    });
    
    // Convert minutes to hours with 2 decimal places
    const totalHours = totalMinutes / 60;
    return totalHours.toFixed(2);
  };
  
  const totalActiveHours = calculateTotalActiveHours();

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#0D47A1' }}>
          User Activity Tracking
        </Typography>
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchActivities} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <HistoryIcon sx={{ mr: 1, color: '#0D47A1' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Total Sessions
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#0D47A1' }}>
                {totalSessions}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccessTimeIcon sx={{ mr: 1, color: '#1976D2' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Active Sessions
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976D2' }}>
                {activeSessions}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PersonIcon sx={{ mr: 1, color: '#388E3C' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Unique Users
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#388E3C' }}>
                {totalUsers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccessTimeIcon sx={{ mr: 1, color: '#7B1FA2' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Total Active Hours
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#7B1FA2' }}>
                {totalActiveHours}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                hours
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ComputerIcon sx={{ mr: 1, color: '#F57C00' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Total Activities
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#F57C00' }}>
                {activities.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterListIcon sx={{ mr: 1, color: '#0D47A1' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>User</InputLabel>
              <Select
                value={filterUserId}
                label="User"
                onChange={(e) => setFilterUserId(e.target.value)}
              >
                <MenuItem value="">All Users</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.fullName} ({u.username})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Username"
              value={filterUsername}
              onChange={(e) => setFilterUsername(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={filterRole}
                label="Role"
                onChange={(e) => setFilterRole(e.target.value)}
              >
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="ADMIN">Admin</MenuItem>
                <MenuItem value="LOAN_OFFICER">Loan Officer</MenuItem>
                <MenuItem value="FIELD_OFFICER">Field Officer</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={12}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={fetchActivities}
                sx={{ bgcolor: '#0D47A1', '&:hover': { bgcolor: '#1565C0' } }}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
              <Button
                variant={showActiveOnly ? 'contained' : 'outlined'}
                onClick={() => {
                  setShowActiveOnly(!showActiveOnly);
                  setTimeout(fetchActivities, 100);
                }}
                color="primary"
              >
                {showActiveOnly ? 'Show All' : 'Show Active Only'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Activities Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : activities.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No user activities found
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#0D47A1' }}>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>User</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Role</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Login Time</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Logout Time</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Duration</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>IP Address</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Location</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Device</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Last Active</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activities.map((activity) => (
                <TableRow key={activity.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {activity.user?.fullName || activity.username}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {activity.username}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getRoleLabel(activity.userRole)}
                      color={getRoleColor(activity.userRole)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(activity.loginTime)}</TableCell>
                  <TableCell>
                    {activity.logoutTime ? formatDateTime(activity.logoutTime) : '-'}
                  </TableCell>
                  <TableCell>
                    {activity.sessionDurationMinutes
                      ? formatDuration(activity.sessionDurationMinutes)
                      : activity.isActive
                      ? 'Active'
                      : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationOnIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      {activity.ipAddress || 'N/A'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {activity.locationAddress ? (
                      <Tooltip 
                        title={
                          <Box>
                            {activity.city && <div>City: {activity.city}</div>}
                            {activity.region && <div>Region: {activity.region}</div>}
                            {activity.country && <div>Country: {activity.country}</div>}
                            {activity.latitude && activity.longitude && (
                              <div>Coordinates: {activity.latitude.toFixed(4)}, {activity.longitude.toFixed(4)}</div>
                            )}
                          </Box>
                        }
                        arrow
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationOnIcon sx={{ fontSize: 16, mr: 0.5, color: 'primary.main' }} />
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {activity.locationAddress}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Unknown
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={activity.deviceInfo || 'N/A'}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {activity.deviceInfo || 'N/A'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {activity.lastActiveTime ? formatDateTime(activity.lastActiveTime) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={activity.isActive ? 'Active' : 'Closed'}
                      color={activity.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(activity)}
                        color="primary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1, color: '#0D47A1' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Activity Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedActivity && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  User
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedActivity.user?.fullName || selectedActivity.username}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Username
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedActivity.username}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Role
                </Typography>
                <Chip
                  label={getRoleLabel(selectedActivity.userRole)}
                  color={getRoleColor(selectedActivity.userRole)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={selectedActivity.isActive ? 'Active' : 'Closed'}
                  color={selectedActivity.isActive ? 'success' : 'default'}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Login Time
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedActivity.loginTime)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Logout Time
                </Typography>
                <Typography variant="body1">
                  {selectedActivity.logoutTime
                    ? formatDateTime(selectedActivity.logoutTime)
                    : 'Session still active'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Session Duration
                </Typography>
                <Typography variant="body1">
                  {selectedActivity.sessionDurationMinutes
                    ? formatDuration(selectedActivity.sessionDurationMinutes)
                    : selectedActivity.isActive
                    ? 'Active session'
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Last Active Time
                </Typography>
                <Typography variant="body1">
                  {selectedActivity.lastActiveTime
                    ? formatDateTime(selectedActivity.lastActiveTime)
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  IP Address
                </Typography>
                <Typography variant="body1">
                  {selectedActivity.ipAddress || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocationOnIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  Location Information
                </Typography>
                {selectedActivity.locationAddress ? (
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      {selectedActivity.locationAddress}
                    </Typography>
                    <Grid container spacing={1}>
                      {selectedActivity.city && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">City:</Typography>
                          <Typography variant="body2">{selectedActivity.city}</Typography>
                        </Grid>
                      )}
                      {selectedActivity.region && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Region:</Typography>
                          <Typography variant="body2">{selectedActivity.region}</Typography>
                        </Grid>
                      )}
                      {selectedActivity.country && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Country:</Typography>
                          <Typography variant="body2">
                            {selectedActivity.country}
                            {selectedActivity.countryCode && ` (${selectedActivity.countryCode})`}
                          </Typography>
                        </Grid>
                      )}
                      {selectedActivity.postalCode && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Postal Code:</Typography>
                          <Typography variant="body2">{selectedActivity.postalCode}</Typography>
                        </Grid>
                      )}
                      {selectedActivity.latitude && selectedActivity.longitude && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Coordinates:</Typography>
                          <Typography variant="body2">
                            {selectedActivity.latitude.toFixed(6)}, {selectedActivity.longitude.toFixed(6)}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<LocationOnIcon />}
                            href={`https://www.google.com/maps?q=${selectedActivity.latitude},${selectedActivity.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ mt: 1 }}
                          >
                            View on Map
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Location information not available
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Device / Browser Information
                </Typography>
                <Typography variant="body1">
                  {selectedActivity.deviceInfo || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserActivityTracking;
